import { wrapMiddlewaresWithSentry } from '@sentry/tanstackstart-react';
import { blogService } from "@acme/api";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { object, optional, string } from "valibot";
import { dbMiddleware } from "~/lib/middleware/db";
import { renderArticleContent } from "~/lib/server/article-html";
import {
  loadArticlePreview,
  setPreviewResponseHeaders,
} from "~/lib/server/blog-preview";
import { baseLocale, isLocale } from "~/paraglide/runtime.js";

function resolveLocale(locale?: string): string {
  return locale && isLocale(locale) ? locale : baseLocale;
}

export const $getAllPublicArticles = createServerFn({ method: "GET" })
  .middleware(wrapMiddlewaresWithSentry({ dbMiddleware }))
  .validator(optional(object({ locale: optional(string()) })))
  .handler((ctx) =>
    blogService.getAllPublic(ctx.context.db, {
      locale: resolveLocale(ctx.data?.locale),
    })
  );

export const $getArticleBySlug = createServerFn({ method: "GET" })
  .middleware(wrapMiddlewaresWithSentry({ dbMiddleware }))
  .validator(object({ locale: optional(string()), slug: string() }))
  .handler(async (ctx) => {
    const article = await blogService.getBySlug(ctx.context.db, {
      locale: resolveLocale(ctx.data.locale),
      slug: ctx.data.slug,
    });
    return { ...article, ...(await renderArticleContent(article.content)) };
  });

/**
 * A reviewer link's article: any article, draft or not, for a valid,
 * unexpired preview token. A bad token throws "Preview not found"; a deleted
 * article throws "Article not found". The route turns both into a 404.
 *
 * The token is read in the handler, which only ever runs on the server, and
 * through `loadArticlePreview`, which only the handler imports: neither the
 * verification code nor the secret behind it reaches the browser bundle.
 */
export const $getArticlePreview = createServerFn({ method: "GET" })
  .middleware(wrapMiddlewaresWithSentry({ dbMiddleware }))
  .validator(object({ locale: optional(string()), token: string() }))
  .handler((ctx) => {
    setPreviewResponseHeaders();
    return loadArticlePreview(ctx.context.db, {
      locale: resolveLocale(ctx.data.locale),
      token: ctx.data.token,
    });
  });

/** What the article page and the reviewer preview both render. */
export type PublicArticle = Awaited<ReturnType<typeof $getArticleBySlug>>;

export const $likeArticle = createServerFn({ method: "POST" })
  .middleware(wrapMiddlewaresWithSentry({ dbMiddleware }))
  .validator(object({ articleId: string() }))
  .handler((ctx) =>
    blogService.like(ctx.context.db, ctx.data, getRequest().headers)
  );

export const $isArticleLiked = createServerFn({ method: "GET" })
  .middleware(wrapMiddlewaresWithSentry({ dbMiddleware }))
  .validator(object({ articleId: string() }))
  .handler((ctx) =>
    blogService.isLiked(ctx.context.db, ctx.data, getRequest().headers)
  );

export const $viewArticle = createServerFn({ method: "POST" })
  .middleware(wrapMiddlewaresWithSentry({ dbMiddleware }))
  .validator(object({ articleId: string() }))
  .handler((ctx) => blogService.view(ctx.context.db, ctx.data));
